// AssetCard — single asset tile. PlayCanvas-style: big square icon/preview
// on top, name + meta underneath, hover reveals Test/Delete actions.
//
// The preview swaps by kind via <AssetIcon> — texture renders the actual
// image, audio renders a waveform, body/face/prefab/rig render a glyph.

import React, { useEffect, useRef, useState } from 'react'
import AssetIcon from './AssetIcon'
import DirtyBadge from '../DirtyBadge'
import { assetKindColor, assetKindShortLabel, blueprintGraphShortLabel } from '../../lib/assetKindColors'

const C = {
  bg:       'var(--m-panel, #11131b)',
  bgHover:  'var(--m-panel-hi, #1a1d28)',
  border:   'var(--m-line-hi, #2a2c36)',
  text:     'var(--m-fg, #e1e1ed)',
  dim:      'var(--m-fg-2, #8f8fa2)',
  primary:  'var(--m-accent, #bec2ff)',
}

export default function AssetCard({
  metrics, asset, onDelete, onSelect, onContextMenu, onOpenGraph, onEdit, onOpenBlueprintGraph, opening, selected, renaming, onCommitRename, onCancelRename }) {
  const [hover, setHover] = useState(false)
  // Double-click acknowledgement. A counter, not a boolean: re-keying the
  // animated wrapper on it is what RESTARTS a CSS animation, so a second
  // double-click bounces again instead of doing nothing because the same
  // animation name is already assigned.
  const [ack, setAck] = useState(0)
  const color = assetKindColor(asset.kind)
  // Default to the large-icon metrics so a caller that doesn't pass any
  // (or a future one) still renders the shipped layout rather than nothing.
  const m = metrics ?? { thumb: 64, pad: 10, fontSize: 11, showMeta: true }
  const details = m.cell === null

  const handleDoubleClick = () => {
    // Graph materials open in a full-screen editor; prefabs open the edit form;
    // a blueprint row IS one graph, so it opens that graph's editor — the same
    // action the outliner's row and the inspector button perform.
    let dispatched = true
    if (asset.kind === 'material' && asset.materialType === 'graph' && onOpenGraph) {
      onOpenGraph(asset)
    } else if (asset.kind === 'prefab' && onEdit) {
      onEdit(asset)
    } else if (asset.kind === 'blueprint_asset' && onOpenBlueprintGraph) {
      onOpenBlueprintGraph(asset)
    } else {
      dispatched = false
    }
    // Bounce only when something was actually dispatched. A card whose kind has
    // no open action must stay still: animating it would claim an open that is
    // never coming, which is worse than the silence it replaces.
    if (dispatched) setAck((n) => n + 1)
  }

  return (
    <div
      draggable={!renaming}
      onDragStart={e => {
        if (renaming) { e.preventDefault(); return }
        e.dataTransfer.setData('text/aos-asset-id', asset.id)
        // 'copyMove' so the drop target can declare dropEffect='copy' (binding
        // a texture to a slot is conceptually a copy/link, not a move). With
        // effectAllowed='move', browsers show the not-allowed cursor whenever
        // the target wants a different effect, and the drop event is silently
        // rejected — even though the dragover handler called preventDefault.
        e.dataTransfer.effectAllowed = 'copyMove'
      }}
      onClick={ev => onSelect?.(ev)}
      onDoubleClick={handleDoubleClick}
      onContextMenu={ev => { ev.preventDefault(); ev.stopPropagation(); onContextMenu?.(asset, ev.clientX, ev.clientY) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        background: selected ? 'var(--m-panel-hi, #1a1f3a)' : (hover ? C.bgHover : C.bg),
        // A details ROW is a list line, not a tile: no per-item border (which
        // would read as 30 stacked boxes), just a hairline separator.
        border: details
          ? 'none' : `1px solid ${selected ? C.primary : C.border}`,
        borderBottom: details ? `1px solid ${C.border}` : undefined,
        // Tiles are Studio cards: token radius + soft card shadow (gass
        // .stu-card). Details rows stay flat list lines.
        borderRadius: details ? 0 : 'var(--m-radius)',
        boxShadow: details ? 'none' : 'var(--m-shadow-card)',
        transition: 'background var(--m-ease), border-color var(--m-ease)',
        padding: details ? `${m.pad}px 12px` : m.pad,
        display: 'flex',
        flexDirection: details ? 'row' : 'column',
        alignItems: 'center',
        gap: details ? 10 : 0,
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      {/* Lighting-bake badge (environments only) — mirrors the viewport chip. */}
      {asset.kind === 'environment' && <BakeBadge status={asset.bakeStatus} />}

      {/* Folder state. Bottom-left so it never collides with BakeBadge
          (top-left) or the hover delete (top-right). Two things surface here
          and both used to be console-only:
            moveState  — a folder move that did not persist, or is in flight.
            folderState — the server row states no folder_path, so this card is
                          at root because nothing told us where it belongs.
          UX proximity rule: the badge sits on the thing it describes. */}
      {(asset.moveState || asset.folderState) && (
        <div style={{ position: 'absolute', bottom: 6, left: 6, zIndex: 1 }}
             onClick={e => e.stopPropagation()}>
          <DirtyBadge
            state={asset.moveState ?? asset.folderState}
            message={asset.moveMessage ?? asset.folderMessage}
            size={9}
          />
        </div>
      )}

      {/* Which blueprint the character LOADS. Only the default is marked: a
          badge on every card would make the one that matters harder to find,
          and the flag comes off the row rather than being inferred here. */}
      {asset.kind === 'blueprint_asset' && asset.isDefault && <DefaultBadge details={details} />}

      {/* Hero icon, at the mode's thumb size (PlayCanvas: 64 large / 40 small
          / 20 leading in details).

          The icon is what bounces, not the card: a row that jumps shoves its
          neighbours' text around, and the dock-bounce idiom this borrows moves
          the ICON. One cycle on a landed double-click, repeating while the open
          is still in flight — see minVisibleDuration.js for why a fast open
          still gets a full cycle. */}
      <div
        key={ack}
        style={{
          flex: 'none',
          animation: opening
            ? 'aos-card-bounce 0.62s ease-in-out infinite'
            : ack ? 'aos-card-bounce 0.42s ease-out 1' : undefined,
        }}
      >
        <AssetIcon asset={asset} size={m.thumb} />
      </div>

      {/* Name — inline-editable when renaming (right-click → Rename / F2) */}
      {renaming ? (
        <RenameInput
          initial={asset.name}
          onCommit={onCommitRename}
          onCancel={onCancelRename}
        />
      ) : (
        <div style={{
          color: C.text, fontFamily: 'var(--m-font)', fontSize: m.fontSize,
          marginTop: details ? 0 : 6,
          width: details ? undefined : '100%',
          flex: details ? 1 : undefined,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textAlign: details ? 'left' : 'center',
        }} title={asset.name}>
          {asset.name}
        </div>
      )}

      {/* Kind dot — small, replaces the loud rectangular badge. Dropped in the
          small-icon mode: there is no room under a 40px thumb, and AssetIcon
          already colour-codes the kind. */}
      {m.showMeta && (
        <div style={{
          color: color, fontFamily: 'var(--m-font)', fontSize: 8.5, fontWeight: 700,
          letterSpacing: '.12em',
          marginTop: details ? 0 : 3,
          flex: 'none', textAlign: details ? 'right' : 'center',
          minWidth: details ? 96 : undefined,
        }}>
          ● {asset.kind === 'blueprint_asset'
            ? blueprintGraphShortLabel(asset.graphKind)
            : assetKindShortLabel(asset.kind)}
        </div>
      )}

      {/* Meta — second line in a grid, trailing column in details */}
      {m.showMeta && <Meta asset={asset} details={details} />}

      {/* Motion, defined next to the thing that uses it and only while it is
          animating — so a card mounted by some future parent animates without
          that parent having to remember to ship keyframes, and an idle grid of
          150 cards emits no <style> at all.

          Reduced motion REDEFINES the same animation name rather than removing
          it: this feedback exists because a fast open was invisible, so an
          accessibility setting must change HOW we answer the double-click, not
          whether we answer it. */}
      {(ack > 0 || opening) && (
        <style>{`
          @keyframes aos-card-bounce {
            0%   { transform: translateY(0)    scale(1); }
            28%  { transform: translateY(-22%) scale(1.06); }
            55%  { transform: translateY(0)    scale(0.98); }
            78%  { transform: translateY(-8%)  scale(1.01); }
            100% { transform: translateY(0)    scale(1); }
          }
          @keyframes aos-card-open-stripe { from { left: -35%; } to { left: 100%; } }
          @media (prefers-reduced-motion: reduce) {
            @keyframes aos-card-bounce { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
            @keyframes aos-card-open-stripe { 0%, 100% { left: 0; opacity: 0.25; } 50% { left: 0; opacity: 1; } }
          }
        `}</style>
      )}

      {/* Indeterminate stripe on the card's own bottom edge while the open is
          in flight. The bounce says "your double-click landed"; this says "and
          it is still working" — two different questions, so two indicators. */}
      {opening && (
        <div data-testid="asset-card-opening" style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: 2,
          overflow: 'hidden', borderRadius: details ? 0 : '0 0 var(--m-radius) var(--m-radius)',
          background: 'color-mix(in srgb, var(--m-accent) 18%, transparent)',
          pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: '35%',
            background: 'var(--m-accent)',
            animation: 'aos-card-open-stripe 0.9s linear infinite',
          }} />
        </div>
      )}

      {/* Hover actions — overlay so they don't add to the card height.
          Test moved into the right-click menu; only delete remains here. */}
      {(hover || selected) && onDelete && (
        <div style={{
          position: 'absolute', top: 6, right: 6,
          display: 'flex', gap: 4,
        }} onClick={e => e.stopPropagation()}>
          <IconBtn label="🗑" title="Delete" onClick={() => onDelete(asset)} danger />
        </div>
      )}
    </div>
  )
}

// Marks the blueprint a character loads. Deliberately the same shape as
// BakeBadge — one badge vocabulary in the grid, not a second one.
// In the icon modes the badge floats over the thumbnail's top-left corner. In
// DETAILS mode the row is horizontal and that same corner is where the name
// starts, so an absolute badge sits on top of it and hides the very thing the
// row exists to show — it read as "★ Default" with the name behind it. There it
// goes in the flow instead, ahead of the name.
function DefaultBadge({ details = false }) {
  return (
    <div style={{
      ...(details
        ? { position: 'static', flex: 'none', marginRight: 6 }
        : { position: 'absolute', top: 6, left: 6 }),
      zIndex: 1,
      background: 'color-mix(in srgb, var(--m-online) 18%, var(--m-panel))',
      color: 'var(--m-online)',
      border: '1px solid color-mix(in srgb, var(--m-online) 45%, transparent)',
      borderRadius: 999, padding: '1px 7px',
      fontFamily: 'var(--m-font)', fontSize: 8.5, fontWeight: 700, letterSpacing: '.04em',
    }} title="This is the blueprint that loads with the character">
      ★ Default
    </div>
  )
}

// Small top-left bake-state badge for environment cards. 'none' shows nothing
// loud — just a dim "unbaked" hint — so baked envs stand out.
function BakeBadge({ status }) {
  const map = {
    ready:  { bg: '#11331f', fg: '#22c55e', border: '#22c55e55', label: '✓ Baked' },
    baking: { bg: '#241f3a', fg: '#a78bfa', border: '#8b6cff55', label: '… Baking' },
    error:  { bg: '#3a1414', fg: '#f87171', border: '#f8717155', label: '⚠ Bake failed' },
    none:   { bg: '#15171f', fg: '#6e7079', border: '#2a2c36', label: 'Unbaked' },
  }
  const s = map[status] ?? map.none
  return (
    <div style={{
      position: 'absolute', top: 6, left: 6, zIndex: 1,
      background: s.bg, color: s.fg, border: `1px solid ${s.border}`,
      borderRadius: 999, padding: '1px 7px',
      fontFamily: 'var(--m-font)', fontSize: 8.5, fontWeight: 700, letterSpacing: '.04em',
    }} title={`Lighting: ${status ?? 'none'}`}>
      {s.label}
    </div>
  )
}

function Meta({ asset, details = false }) {
  const parts = []
  if (asset.durationMs != null) parts.push(`${(asset.durationMs/1000).toFixed(2)}s`)
  if (asset.additive) parts.push('additive')
  if (asset.loopable) parts.push('loop')
  if (asset.tag) parts.push(`#${asset.tag}`)
  if (!parts.length) return null
  return (
    <div style={{
      color: 'var(--m-fg-3, #6e7079)', fontFamily: 'var(--m-font)', fontSize: 9,
      marginTop: details ? 0 : 2,
      textAlign: details ? 'right' : 'center',
      flex: details ? 'none' : undefined,
      minWidth: details ? 90 : undefined,
      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }} title={parts.join(' · ')}>
      {parts.join(' · ')}
    </div>
  )
}

// Inline name editor, mirroring FolderTree's RenameInput: autofocus + select,
// Enter/blur commits, Escape cancels. Stops propagation so clicks/keys don't
// bubble to the card (select/drag). A blank or unchanged name is a cancel.
function RenameInput({ initial, onCommit, onCancel }) {
  const [value, setValue] = useState(initial)
  const ref = useRef(null)
  useEffect(() => { ref.current?.focus(); ref.current?.select() }, [])
  const done = () => {
    const next = value.trim()
    if (!next || next === initial) onCancel?.()
    else onCommit?.(next)
  }
  return (
    <input
      ref={ref}
      data-testid="asset-rename-input"
      value={value}
      onChange={e => setValue(e.target.value)}
      onClick={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
      onBlur={done}
      onKeyDown={e => {
        e.stopPropagation()
        if (e.key === 'Enter') done()
        else if (e.key === 'Escape') onCancel?.()
      }}
      style={{
        marginTop: 8, width: '100%', boxSizing: 'border-box',
        background: 'var(--m-panel-hi, #080a10)', color: C.text,
        border: '1px solid var(--m-accent, #bec2ff)', borderRadius: 'var(--m-radius-sm)',
        padding: '3px 6px', textAlign: 'center',
        fontFamily: 'var(--m-font)', fontSize: 11, outline: 'none',
      }}
    />
  )
}

function IconBtn({ label, title, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: '#0c0e15dd',
        border: `1px solid ${danger ? '#f8717155' : 'var(--m-line-hi, #2a2c36)'}`,
        color: danger ? '#f87171' : 'var(--m-accent, #bec2ff)',
        borderRadius: '50%',
        width: 22, height: 22,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, cursor: 'pointer', padding: 0,
        backdropFilter: 'blur(4px)',
      }}
    >{label}</button>
  )
}
